import React, { useEffect, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { Tab } from "@headlessui/react";
import ModalIntelliPay from "./modal-intellipay";
import OffersStep from "./OffersStep";
import TshirtAndPositionStep from "../../../components/TshirtAndPositionStep";
import { useAxiosInstance } from "../../../services/Interceptor";

import { positions, tshirtSizes } from "../../../utils/PositionStateData";
import {
  getAthleteProfile,
  updateAthleteProfile,
} from "../../../services/AthleteService";
import { toast, ToastContainer } from "react-toastify";

export default function RegisterFlowModal({
  open,
  onClose,
  athleteId,
  athleteEmail,
  eventId,
  origin,
  currentPosition,
}) {
  const apiInstance = useAxiosInstance();

  const [activeTab, setActiveTab] = useState(0);
  const [showPayment, setShowPayment] = useState(false);

  const [tshirtSize, setTshirtSize] = useState("");
  const [position, setPosition] = useState(currentPosition || "");
  const [originalPosition, setOriginalPosition] = useState(
    currentPosition || "",
  );
  const [saving, setSaving] = useState(false);
  const [offerAcceptanceStatus, setOfferAcceptanceStatus] = useState("no");
  const [offerAcceptanceSchool, setOfferAcceptanceSchool] = useState("");

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setShowPayment(false);
      setTshirtSize("");
      setOfferAcceptanceStatus("no");
      setOfferAcceptanceSchool("");
    }
  }, [open, currentPosition]);

  useEffect(() => {
    if (currentPosition) return;

    async function fetchData() {
      const response = await getAthleteProfile(apiInstance, athleteId);
      setPosition(response.primaryPosition);
      setOriginalPosition(response.primaryPosition);
    }
    fetchData();
  }, [apiInstance, athleteId, currentPosition]);

  const canContinueToPayment = !!tshirtSize && !!position;

  const saveAndContinueToPayment = async () => {
    if (!canContinueToPayment) return;

    setSaving(true);
    try {
      if (position && position !== originalPosition) {
        await updateAthleteProfile(apiInstance, athleteId, {
          primaryPosition: position,
        });
        toast.success("Position updated successfully");
      }

      setShowPayment(true);
    } catch (err) {
      toast.error("Failed to update registration details. Please try again.");
      throw err;
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!showPayment) onClose();
      }}
      className="relative z-[250]"
    >
      <DialogBackdrop className="fixed inset-0 bg-black/50" />

      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        {!showPayment ? (
          <DialogPanel className="w-full max-w-5xl bg-white rounded-lg shadow-xl overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-gray-900">
                  Complete registration details
                </div>
                <div className="text-sm text-gray-600">
                  Step: {activeTab + 1} of 3
                </div>
              </div>

              <button
                type="button"
                className="text-gray-500 hover:bg-gray-100 rounded-full p-2"
                onClick={onClose}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-5">
              <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                <Tab.List className="flex gap-2 border-b">
                  {["Offers", "T-Shirt Size", "Position"].map((label) => (
                    <Tab
                      key={label}
                      className={({ selected }) =>
                        `px-4 py-2 text-sm font-semibold -mb-[1px] border-b-2 ${
                          selected
                            ? "border-blue-600 text-blue-700"
                            : "border-transparent text-gray-600 hover:text-gray-900"
                        }`
                      }
                    >
                      {label}
                    </Tab>
                  ))}
                </Tab.List>

                <Tab.Panels className="mt-4">
                  <Tab.Panel unmount={false}>
                    <OffersStep
                      athleteId={athleteId}
                      onDone={(status, school) => {
                        setOfferAcceptanceStatus(status);
                        setOfferAcceptanceSchool(school);
                        setActiveTab(1);
                      }}
                    />
                  </Tab.Panel>

                  <Tab.Panel unmount={false}>
                    <TshirtAndPositionStep
                      mode="tshirt"
                      tshirtSize={tshirtSize}
                      setTshirtSize={setTshirtSize}
                      tshirtSizes={tshirtSizes}
                      onBack={() => setActiveTab(0)}
                      onNext={() => setActiveTab(2)}
                    />
                  </Tab.Panel>

                  <Tab.Panel unmount={false}>
                    <TshirtAndPositionStep
                      mode="position"
                      position={position}
                      setPosition={setPosition}
                      positions={positions}
                      onBack={() => setActiveTab(1)}
                      onContinue={saveAndContinueToPayment}
                      saving={saving}
                      canContinue={canContinueToPayment}
                    />
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
            </div>
          </DialogPanel>
        ) : (
          <DialogPanel className="w-full max-w-4xl bg-transparent shadow-none">
            <ModalIntelliPay
              athleteId={athleteId}
              athleteEmail={athleteEmail}
              eventId={eventId}
              origin={origin}
              onCloseAll={onClose}
              tShirtSize={tshirtSize}
              offerAcceptanceStatus={offerAcceptanceStatus}
              offerAcceptanceSchool={offerAcceptanceSchool}
            />
          </DialogPanel>
        )}
      </div>
      <ToastContainer />
    </Dialog>
  );
}
